import React, { useState } from 'react';
import { Card, Statistic, Modal, Table, Tag } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { GaugeChart, LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { CancelReasonOptions } from '../../../constants/picking';

echarts.use([GaugeChart, LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
  neutral: '#8C8C8C',
  brand: '#00A651',
  brandYellow: '#FFD100',
};

/**
 * Module 1: Mission Control - Core alerts and global overview
 * SLA Gauge + KPI Cards + Timeout/Cancel blinking alerts + Sparkline
 */
const MissionControl = ({ data }) => {
  const { t } = useTranslation();
  const [timeoutModalVisible, setTimeoutModalVisible] = useState(false);
  const [cancelledModalVisible, setCancelledModalVisible] = useState(false);
  const [returnedModalVisible, setReturnedModalVisible] = useState(false);

  if (!data) return null;

  // Table column definitions for Timeout Orders modal
  const timeoutColumns = [
    { title: t('dashboard.missionControl.orderId'), dataIndex: 'orderId', key: 'orderId', width: 180 },
    { title: t('dashboard.missionControl.picker'), dataIndex: 'pickerName', key: 'pickerName', width: 120 },
    {
      title: t('dashboard.missionControl.status'), dataIndex: 'status', key: 'status', width: 100,
      render: (status) => {
        const colorMap = { Picking: 'blue', Packing: 'cyan', Waiting: 'orange' };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
    },
    { title: t('dashboard.missionControl.createTime'), dataIndex: 'createTime', key: 'createTime', width: 170 },
    {
      title: t('dashboard.missionControl.timeoutMin'), dataIndex: 'timeoutMinutes', key: 'timeoutMinutes', width: 120,
      sorter: (a, b) => a.timeoutMinutes - b.timeoutMinutes,
      render: (minutes) => <span style={{ color: minutes >= 15 ? COLORS.danger : COLORS.warning, fontWeight: 'bold' }}>{minutes}</span>,
    },
  ];

  // Table column definitions for Cancelled Orders modal
  const cancelledColumns = [
    { title: t('dashboard.missionControl.orderId'), dataIndex: 'orderId', key: 'orderId', width: 180 },
    { title: t('dashboard.missionControl.customer'), dataIndex: 'customerName', key: 'customerName', width: 120 },
    {
      title: t('dashboard.missionControl.cancelReason'), dataIndex: 'cancelReason', key: 'cancelReason', width: 200,
      render: (reason) => {
        if (!reason) return '-';
        const opt = CancelReasonOptions.find((o) => o.value === reason);
        return <Tag color="red" style={{ whiteSpace: 'nowrap' }}>{opt ? t(opt.label) : reason}</Tag>;
      },
    },
    { title: t('dashboard.missionControl.cancelledTime'), dataIndex: 'cancelledTime', key: 'cancelledTime', width: 170 },
    {
      title: t('dashboard.missionControl.amount'), dataIndex: 'totalAmount', key: 'totalAmount', width: 100, align: 'right',
      render: (amount) => `฿${amount.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`,
    },
    { title: t('dashboard.missionControl.skus'), dataIndex: 'skuCount', key: 'skuCount', width: 70, align: 'center' },
  ];

  // Table column definitions for Returned Orders modal
  const returnedColumns = [
    { title: t('dashboard.missionControl.orderId'), dataIndex: 'orderId', key: 'orderId', width: 180 },
    { title: t('dashboard.missionControl.customer'), dataIndex: 'customerName', key: 'customerName', width: 120 },
    {
      title: t('dashboard.missionControl.returnReason'), dataIndex: 'returnReason', key: 'returnReason', width: 160,
      render: (reason) => <Tag color="orange">{reason}</Tag>,
    },
    { title: t('dashboard.missionControl.returnTime'), dataIndex: 'returnTime', key: 'returnTime', width: 170 },
    {
      title: t('dashboard.missionControl.amount'), dataIndex: 'totalAmount', key: 'totalAmount', width: 100, align: 'right',
      render: (amount) => `฿${amount.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`
    },
    {
      title: t('dashboard.missionControl.status'), dataIndex: 'status', key: 'status', width: 100,
      render: (status) => <Tag color={status === 'Processed' ? 'green' : 'orange'}>{status}</Tag>,
    },
  ];

  // SLA Gauge config
  const slaColor = data.orderCompleteRate >= 95 ? COLORS.success : data.orderCompleteRate >= 90 ? COLORS.warning : COLORS.danger;
  const gaugeOption = {
    series: [{
      type: 'gauge',
      startAngle: 90,
      endAngle: -270,
      pointer: { show: false },
      progress: {
        show: true,
        overlap: false,
        roundCap: true,
        clip: false,
        itemStyle: { color: slaColor },
      },
      axisLine: { lineStyle: { width: 16, color: [[1, '#f0f0f0']] } },
      splitLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      data: [{
        value: data.orderCompleteRate,
        detail: {
          fontSize: 24,
          fontWeight: 'bold',
          offsetCenter: [0, -8],
          formatter: '{value}%',
          color: slaColor,
        },
      }],
      detail: { color: 'inherit' },
      title: {
        fontSize: 13,
        offsetCenter: [0, '55%'],
        color: '#666',
      },
    }],
  };

  // Sparkline for avg SKU per order trend
  const sparklineOption = {
    grid: { top: 4, right: 4, bottom: 18, left: 4 },
    xAxis: {
      type: 'category',
      show: true,
      data: Array.from({ length: 24 }, (_, i) => `${i}:00`),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { fontSize: 9, color: '#999', interval: 5 },
    },
    yAxis: { type: 'value', show: false },
    tooltip: { trigger: 'axis', formatter: (params) => `${params[0].name}: ${params[0].value} orders` },
    series: [{
      type: 'line',
      data: data.hourlyOrders,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2, color: COLORS.brand },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(0,166,81,0.25)' },
          { offset: 1, color: 'rgba(0,166,81,0.02)' },
        ]),
      },
    }],
  };

  return (
    <>
      <style>{`
        @keyframes danger-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(255,77,79,0.3); }
          50% { opacity: 0.75; box-shadow: 0 0 20px rgba(255,77,79,0.6); }
        }
        .danger-blink {
          animation: danger-pulse 2s ease-in-out infinite;
        }
        .danger-blink:hover {
          animation: none;
          transform: scale(1.02);
          transition: transform 0.2s;
        }
      `}</style>

      <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', flexWrap: 'wrap' }}>
        {/* SLA Gauge */}
        <Card
          style={{ width: 200, flexShrink: 0 }}
          styles={{ body: { padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' } }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 4 }}>{t('dashboard.missionControl.slaFulfillment')}</div>
          <ReactEChartsCore echarts={echarts} option={gaugeOption} style={{ height: 160, width: 180 }} opts={{ renderer: 'canvas' }} />
        </Card>

        {/* KPI Cards */}
        <div style={{ flex: '1 1 400px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Top row: Orders + Items */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Card style={{ flex: 1 }} styles={{ body: { padding: '12px 16px' } }}>
              <Statistic
                title={t('dashboard.missionControl.todayTotalOrders')}
                value={data.todayTotalOrders}
                valueStyle={{ fontSize: 28, fontWeight: 'bold', color: '#1890ff' }}
              />
              <ReactEChartsCore echarts={echarts} option={sparklineOption} style={{ height: 40, marginTop: 4 }} opts={{ renderer: 'canvas' }} />
              {/* Normal orders metric */}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: 12, color: '#999' }}>{t('dashboard.missionControl.normalOrders')}</div>
                <div style={{ fontSize: 18, fontWeight: 'bold', color: COLORS.success }}>{data.normalOrders.toLocaleString()}</div>
              </div>
            </Card>
            <Card style={{ flex: 1 }} styles={{ body: { padding: '12px 16px' } }}>
              <Statistic
                title={t('dashboard.missionControl.todayTotalItems')}
                value={data.todayTotalItems}
                valueStyle={{ fontSize: 28, fontWeight: 'bold', color: COLORS.brand }}
              />
              <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                <span>{t('dashboard.missionControl.totalSKUs')}: <b>{data.totalSKUs?.toLocaleString() || '-'}</b></span>
              </div>
              {/* Avg SKU/Order and Avg item/order metrics */}
              <div style={{ display: 'flex', gap: 16, marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#999' }}>{t('dashboard.missionControl.avgSKUPerOrder')}</div>
                  <div style={{ fontSize: 18, fontWeight: 'bold', color: '#333' }}>{data.avgSKUPerOrder}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#999' }}>{t('dashboard.missionControl.avgItemPerOrder')}</div>
                  <div style={{ fontSize: 18, fontWeight: 'bold', color: '#333' }}>{data.complexityPerOrder}</div>
                </div>
              </div>
            </Card>
          </div>

        </div>

        {/* Danger Alerts */}
        <div style={{ display: 'flex', gap: 12, flex: '1 1 auto', minWidth: 0 }}>
          {/* Timeout Orders - RED blinking */}
          <Card
            className="danger-blink"
            style={{
              flex: '1 1 120px',
              minWidth: 120,
              border: '2px solid ' + COLORS.danger,
              background: '#fff2f0',
              cursor: 'pointer',
            }}
            styles={{ body: { padding: 16, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' } }}
            onClick={() => setTimeoutModalVisible(true)}
          >
            <WarningOutlined style={{ fontSize: 24, color: COLORS.danger, marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: COLORS.danger, fontWeight: 600 }}>{t('dashboard.missionControl.timeoutOrders')}</div>
            <div style={{ fontSize: 42, fontWeight: 'bold', color: COLORS.danger, lineHeight: 1.2 }}>
              {data.timeoutOrders}
            </div>
            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{t('dashboard.clickForDetails')}</div>
          </Card>

          {/* Cancelled Orders - RED */}
          <Card
            style={{
              flex: '1 1 120px',
              minWidth: 120,
              border: '2px solid #ff7875',
              background: '#fff2f0',
              cursor: 'pointer',
            }}
            styles={{ body: { padding: 16, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' } }}
            onClick={() => setCancelledModalVisible(true)}
          >
            <WarningOutlined style={{ fontSize: 24, color: '#ff7875', marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: '#ff7875', fontWeight: 600 }}>{t('dashboard.missionControl.cancelledOrders')}</div>
            <div style={{ fontSize: 42, fontWeight: 'bold', color: '#cf1322', lineHeight: 1.2 }}>
              {data.cancelledOrders}
            </div>
            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{t('dashboard.clickForDetails')}</div>
          </Card>

          {/* Returned Orders - Orange with 3 metrics */}
          <Card
            style={{
              flex: '1 1 150px',
              minWidth: 150,
              border: '2px solid #fa8c16',
              background: '#fff7e6',
              cursor: 'pointer',
            }}
            styles={{ body: { padding: 12, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' } }}
            onClick={() => setReturnedModalVisible(true)}
          >
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <WarningOutlined style={{ fontSize: 20, color: '#fa8c16', marginBottom: 4 }} />
              <div style={{ fontSize: 12, color: '#fa8c16', fontWeight: 600 }}>{t('dashboard.missionControl.returnedOrders')}</div>
            </div>
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#d46b08', lineHeight: 1 }}>
                {data.returnedOrders}
              </div>
              <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{t('dashboard.total')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid #ffe7ba' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 'bold', color: '#fa8c16' }}>
                  {data.waitingForHandlingReturnOrders}
                </div>
                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{t('dashboard.missionControl.waiting')}</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 'bold', color: '#52c41a' }}>
                  {data.processedReturnOrders}
                </div>
                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{t('dashboard.missionControl.processed')}</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Timeout Orders Detail Modal */}
      <Modal
        title={<span style={{ color: COLORS.danger }}><WarningOutlined style={{ marginRight: 8 }} />{t('dashboard.missionControl.timeoutOrders')} ({data.timeoutOrders})</span>}
        open={timeoutModalVisible}
        onCancel={() => setTimeoutModalVisible(false)}
        destroyOnClose
        footer={null}
        width={800}
      >
        <Table
          columns={timeoutColumns}
          dataSource={data.timeoutOrderList || []}
          rowKey="orderId"
          size="small"
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* Cancelled Orders Detail Modal */}
      <Modal
        title={<span style={{ color: '#cf1322' }}><WarningOutlined style={{ marginRight: 8 }} />{t('dashboard.missionControl.cancelledOrders')} ({data.cancelledOrders})</span>}
        open={cancelledModalVisible}
        onCancel={() => setCancelledModalVisible(false)}
        destroyOnClose
        footer={null}
        width={900}
      >
        <Table
          columns={cancelledColumns}
          dataSource={data.cancelledOrderList || []}
          rowKey="orderId"
          size="small"
          pagination={{ pageSize: 10, showTotal: (total) => t('dashboard.totalOrders', { total }) }}
          scroll={{ x: 840, y: 400 }}
        />
      </Modal>

      {/* Returned Orders Detail Modal */}
      <Modal
        title={<span style={{ color: '#d46b08' }}><WarningOutlined style={{ marginRight: 8 }} />{t('dashboard.missionControl.returnedOrders')} ({data.returnedOrders})</span>}
        open={returnedModalVisible}
        onCancel={() => setReturnedModalVisible(false)}
        destroyOnClose
        footer={null}
        width={950}
      >
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1, background: '#fff7e6', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#999' }}>{t('dashboard.missionControl.waiting')}</div>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa8c16' }}>{data.waitingForHandlingReturnOrders}</div>
          </div>
          <div style={{ flex: 1, background: '#f6ffed', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#999' }}>{t('dashboard.missionControl.processed')}</div>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>{data.processedReturnOrders}</div>
          </div>
        </div>
        <Table
          columns={returnedColumns}
          dataSource={data.returnedOrderList || []}
          rowKey="orderId"
          size="small"
          pagination={{ pageSize: 10, showTotal: (total) => t('dashboard.totalOrders', { total }) }}
          scroll={{ y: 400 }}
        />
      </Modal>
    </>
  );
};

export default MissionControl;
